import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, BookOpen, BarChart2, Map, Percent, Ticket, MapPin, PhoneCall, ArrowRight } from 'lucide-react';

const WebinarSection: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Features Data with Lucide Icons
  const features = [
    { title: "Win Scholarships, Medals & Certificates", icon: <Trophy className="w-6 h-6 text-white" /> },
    { title: "JEE | NEET | Foundation Smart Prep Bootcamp", icon: <BookOpen className="w-6 h-6 text-white" /> },
    { title: "Live Result & Scorecard Reveal", icon: <BarChart2 className="w-6 h-6 text-white" /> },
    { title: "Get Your Personalized Success Roadmap", icon: <Map className="w-6 h-6 text-white" /> },
    { title: "Exclusive Batch Discounts & Offers", icon: <Percent className="w-6 h-6 text-white" /> },
    { title: "Claim Your ₹99 Exclusive Batch Pass!", icon: <Ticket className="w-6 h-6 text-white" /> },
  ];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 50 } },
  };

  return (
    <div className="relative bg-gradient-to-b from-white via-red-50/30 to-white py-20 px-4 md:px-10 overflow-hidden">
      
      {/* Background Decor Elements */}
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#9e1c22] via-yellow-400 to-[#9e1c22]"></div>
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
      <div className="absolute top-40 -left-20 w-72 h-72 bg-yellow-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>

      <section className="max-w-7xl mx-auto relative z-10">
        
        {/* --- Header Section --- */}
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block bg-[#9e1c22]/10 border border-[#9e1c22]/20 rounded-full px-5 py-1.5 mb-4"
          >
            <span className="text-xs font-bold tracking-widest text-[#9e1c22] uppercase flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span> Live Event
            </span>
          </motion.div>

          <motion.h2 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-black text-gray-900 uppercase tracking-tight leading-tight"
          >
            Join Our Exclusive <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9e1c22] to-[#d92830]">
              Career Guidance Webinar
            </span>
          </motion.h2>
        </div>

        {/* --- Features Grid --- */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
        >
          {features.map((item, index) => (
            <motion.div 
              key={index} 
              // variants={itemVariants}
              whileHover={{ y: -5, boxShadow: "0 20px 40px -10px rgba(158,28,34,0.15)" }}
              className="flex items-center p-4 bg-white rounded-2xl border border-gray-100 shadow-lg transition-all duration-300 group"
            >
              {/* Icon Box */}
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9e1c22] to-[#c0392b] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 shrink-0">
                {item.icon}
              </div>
              
              {/* Text */}
              <div className="ml-5">
                <p className="text-gray-800 font-bold text-sm md:text-base leading-tight group-hover:text-[#9e1c22] transition-colors">
                  {item.title}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* --- CTA Button --- */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <button
            onClick={scrollToTop}
            className="relative group bg-[#9e1c22] text-white font-black text-lg md:text-xl px-12 py-5 rounded-full shadow-[0_10px_30px_rgba(158,28,34,0.4)] hover:shadow-[0_15px_40px_rgba(158,28,34,0.6)] hover:-translate-y-1 transition-all uppercase tracking-wide overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-3">
              Register for Free Now <ArrowRight className="w-6 h-6 animate-bounce-x" />
            </span>
            {/* Shimmer Effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent z-0"></div>
          </button>
          <p className="mt-3 text-gray-500 text-sm font-medium">Limited Seats Available. Hurry Up!</p>
        </motion.div>
      </section>

      {/* --- Footer Address Card --- */}
      <motion.footer 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="mt-24 max-w-5xl mx-auto"
      >
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100 relative overflow-hidden">
          
          {/* Decorative Border Line */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-yellow-400 via-[#9e1c22] to-yellow-400"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center relative z-10">
            
            {/* Address Section */}
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <MapPin className="text-[#9e1c22] w-6 h-6" />
              </div>
              <div>
                <h4 className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Visit Our Center</h4>
                <p className="text-lg md:text-xl font-bold text-gray-800 leading-snug">
                  Plot No. 267, Guru Nanak Mission Chowk, Opp. Guru Nanak Mission Hospital, Lajpat Nagar
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="flex items-start gap-5 md:border-l md:pl-10 border-gray-100">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <PhoneCall className="text-[#9e1c22] w-6 h-6" />
              </div>
              <div>
                <h4 className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Contact Support</h4>
                <p className="text-3xl md:text-4xl font-black text-[#9e1c22] tracking-tight">
                  +91 9456665552
                </p>
                <p className="text-sm text-gray-500 mt-1">Available 10 AM - 7 PM</p>
              </div>
            </div>

          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default WebinarSection;